/* eslint-disable import/no-anonymous-default-export */
import {
    ROOM_CREATED,
    ROOM_STATE,
    ROOM_ERROR,
    ROOM_LEAVE,
    ROOM_JOIN,
    ROOM_START_GAME,
    ROOM_RECONNECT,
    ROOM_RECONNECTING,
    ROOM_MIRROR_JOIN,
    ADD_PLAYER,
    REMOVE_PLAYER,
    UPDATE_PLAYER,
    ADD_AVATAR,
    UPDATE_AVATAR,
} from "actions/types";
import LoggingService from "services/logging";

const initialState = {
    isHost: false,
    isMirror: false,
    roomOpen: false,
    reconnecting: false,
    roomId: "",
    sessionId: "",
    room: {},
    roomState: {},
    roomError: false,
    error: "",
    errorMessage: "",
    errorCode: null,
    player: null,
    startingGame: false,
    redirectInfo: {},
    players: [],
    avatarList: [],
};

export default function (state = initialState, action) {
    const { type, payload } = action;
    switch (type) {
        case ROOM_CREATED:
            return {
                ...state,
                isHost: true,
                isMirror: false,
                roomOpen: true,
                reconnecting: false,
                roomId: payload.room.roomId,
                sessionId: payload.room.sessionId,
                room: payload.room,
                roomState: payload.state,
                roomError: false,
                player: null,
                error: "",
                errorMessage: "",
                errorCode: null
            };
        case ROOM_MIRROR_JOIN:
            return {
                ...state,
                isHost: true,
                isMirror: true,
                roomOpen: true,
                reconnecting: false,
                roomId: payload.room.roomId,
                sessionId: payload.room.sessionId,
                room: payload.room,
                roomState: payload.state,
                roomError: false,
                player: null,
                error: "",
                errorMessage: "",
                errorCode: null
            };
        case ROOM_STATE:
            // Any state message means the WS is alive. If we were showing the
            // "Reconnecting…" overlay, this is the signal that the SDK auto-recovered.
            return {
                ...state,
                roomState: payload.roomState,
                isHost: state.isMirror ? true : payload.roomState.host.id == state.sessionId,
                player: state.isHost ? {} : payload.roomState.players[state.sessionId],
                reconnecting: false,
            };
        case ROOM_ERROR:
            //LoggingService.logError(payload.message, payload.error);
            return {
                ...state,
                isHost: false,
                isMirror: false,
                roomError: true,
                errorMessage: payload.message,
                roomOpen: false,
                reconnecting: false,
                room: {},
                roomId: "",
                sessionId: "",
                roomState: {},
                play: null,
                startingGame: false,
                redirectInfo: {},
                errorCode: payload.errorCode,
                avatarList: []
            };
        case ROOM_LEAVE:
            return {
                ...state,
                isHost: false,
                isMirror: false,
                roomOpen: false,
                reconnecting: false,
                room: {},
                roomId: "",
                sessionId: "",
                roomState: {},
                player: null,
                players: [],
                errorCode: payload.errorCode,
                avatarList: []
            };
        case ROOM_JOIN:
            return {
                ...state,
                isHost: false,
                isMirror: false,
                roomOpen: true,
                reconnecting: false,
                roomId: payload.room.roomId,
                sessionId: payload.room.sessionId,
                room: payload.room,
                roomState: payload.state,
                roomError: false,
                player: payload.state.players[state.sessionId],
                error: "",
                errorMessage: "",
                errorCode: null
            };
        case ROOM_RECONNECT:
            const isReconnectHost = state.isMirror ? true : payload.room.sessionId == payload.state.host.id;
            return {
                ...state,
                isHost: isReconnectHost,
                roomOpen: true,
                reconnecting: false,
                roomId: payload.room.roomId,
                sessionId: payload.room.sessionId,
                room: payload.room,
                roomState: payload.state,
                roomError: false,
                player: isReconnectHost ? null : payload.state.players[state.sessionId],
                error: "",
                errorMessage: "",
                errorCode: null
            };
        case ROOM_RECONNECTING:
            // Hold roomOpen=true so Play.js / Lobby.js don't navigate away while
            // we attempt to recover the seat via client.reconnect(token).
            return {
                ...state,
                reconnecting: true,
            };
        case ROOM_START_GAME: {
            // The generic `game_starting` fallback (sent to a player whose in-flight
            // location_check races the game start) dispatches an internal-style payload:
            // no isExternalGame, no roomCode. For an external game the correct redirect
            // was already set by `redirect_to_external_game` (isExternalGame:true,
            // /client?code=…). Don't let the fallback clobber it — the downgrade produces
            // /client?token=<lobbyReconnectToken>, which external game clients can't read,
            // stranding the player on "No game code provided". Internal games never set
            // isExternalGame, so this guard never triggers for them.
            const incomingExternal = payload.isExternalGame || false;
            if (state.startingGame
                && state.redirectInfo?.isExternalGame
                && !incomingExternal
                && state.redirectInfo.gameId === payload.gameId) {
                return state; // keep the external redirect
            }
            return {
                ...state,
                startingGame: true,
                redirectInfo: {
                    gameURL: payload.gameURL,
                    gameId: payload.gameId,
                    roomId: state.roomId,
                    sessionId: state.sessionId,
                    isHost: state.isHost,
                    reconnectionToken: payload.reconnectionToken,
                    isExternalGame: payload.isExternalGame || false,
                    roomCode: payload.roomCode || null,
                    // External game specific info
                    hostUrl: payload.hostUrl || null,
                    clientUrl: payload.clientUrl || null,
                    serverUrl: payload.serverUrl || null,
                },
            };
        }
        case ADD_PLAYER:
            // Dedupe by id — guards against any future race where onAdd fires
            // twice for the same session, or stale state surviving a flow we
            // haven't reset yet. With the resets on ROOM_JOIN/ROOM_RECONNECT
            // this should never hit, but it's cheap insurance.
            if (state.players.some(p => p.id === payload.player.id)) {
                return state;
            }
            return {
                ...state,
                players: [...state.players, payload.player],
            }
        case REMOVE_PLAYER:
            return {
                ...state,
                players: state.players.filter(x => x.id != payload.player.id),
            }
        case UPDATE_PLAYER:
            let statePlayers = [...state.players];
            let pos = statePlayers.map(function (e) { return e.id; }).indexOf(payload.player.id);

            let statePlayer = { ...state.players[pos] };
            statePlayer[payload.field] = payload.value;
            //payload.changes.forEach(change => {
            //    //console.log(change.field);
            //    //console.log(change.value);
            //    //console.log(change.previousValue);
            //    statePlayer[change.field] = change.value;
            //});
            statePlayers[pos] = statePlayer;
            return {
                ...state,
                players: statePlayers,
            }
        case ADD_AVATAR:
            return {
                ...state,
                avatarList: [...state.avatarList, payload.avatar],
            }
        case UPDATE_AVATAR:
            let stateAvatars = [...state.avatarList];
            let posAvatar = stateAvatars.map(function (e) { return e.id; }).indexOf(payload.avatar.id);
            let stateAvatar = { ...state.avatarList[posAvatar] };
            stateAvatar[payload.field] = payload.value;
            stateAvatars[posAvatar] = stateAvatar;
            return {
                ...state,
                avatarList: stateAvatars,
            }
        default:
            return state;
    }
}
